import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADVISOR_NAV } from "@/pages/advisor/AdvisorHome";
import { downloadElementPdf } from "@/lib/pdf";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GPA_LOGO } from "@/lib/assets";
import {
  Award, PlayCircle, CheckCircle2, XCircle, Loader2, FileDown, Lock, FileText,
  DollarSign, ShieldCheck, RotateCcw, GraduationCap, Upload, ArrowRight, Circle,
} from "lucide-react";

// ---- Course content (training videos are placeholders) --------------------
const MODULES = [
  {
    id: "m1", title: "What the TSB Is (and Isn't)", minutes: 35,
    summary: "The Transferable Skills Benchmark measures 10 durable skills across 3 domains — separate from a GPA, which reflects content mastery.",
    points: ["The 10 transferable skills and the 3 domains they roll up into.", "Why employers value durable skills a transcript can't show.", "How the TSB complements — never replaces — academic grades."],
    quiz: [
      { q: "The TSB primarily measures…", options: ["Course grades", "10 durable transferable skills", "Attendance"], answer: 1 },
      { q: "The TSB is best described as…", options: ["A replacement for the GPA", "A complement to the GPA", "A disciplinary record"], answer: 1 },
    ],
  },
  {
    id: "m2", title: "The 4-Level Developmental Scale", minutes: 40,
    summary: "Each skill is rated Emerging → Developing → Proficient → Advanced. Consistent, evidence-based interpretation is essential.",
    points: ["Descriptors and typical evidence for each of the four levels.", "Calibrating ratings fairly across students and courses.", "Framing levels as growth stages, not fixed labels."],
    quiz: [
      { q: "How many developmental levels are there?", options: ["Two", "Four", "Ten"], answer: 1 },
      { q: "A score of 3.4 corresponds to…", options: ["Emerging", "Proficient", "Advanced"], answer: 1 },
    ],
  },
  {
    id: "m3", title: "How Scores Roll Up", minutes: 30,
    summary: "Skills combine into weighted domains, and domains roll up into the TSB (shown 1.0–4.0 and 0–100).",
    points: ["Skills → domains (weighted) → TSB — in that order.", "How domain weights shape the overall benchmark.", "Reading the TSB gauge and the 0–100 conversion."],
    quiz: [{ q: "The correct roll-up order is…", options: ["TSB → domains → skills", "Skills → domains → TSB", "Domains → skills → TSB"], answer: 1 }],
  },
  {
    id: "m4", title: "Interests vs. Skills, and the Employer Signal", minutes: 35,
    summary: "O*NET RIASEC interests stay separate from the TSB. The employer 360 is a labeled, reversible, individually-weighted signal.",
    points: ["Why career interests (RIASEC) are never merged into the TSB.", "No student PII is ever sent to O*NET.", "How the employer-verified signal is labeled, weighted, and reversible."],
    quiz: [
      { q: "RIASEC interest results should be…", options: ["Averaged into the TSB", "Kept separate from the TSB", "Shown on the Skill Radar"], answer: 1 },
      { q: "The employer 360 signal is…", options: ["A silent overwrite", "Labeled and reversible", "The main benchmark"], answer: 1 },
    ],
  },
  {
    id: "m5", title: "Advising With the Data", minutes: 40,
    summary: "Turn a benchmark into action: identify a focus skill, assign a Learning Academy module, and set a checkpoint.",
    points: ["Reading a student profile for strengths and focus areas.", "Mapping low domains to specific Learning Academy modules.", "Coaching conversations that motivate growth."],
    quiz: [{ q: "The best use of a low domain score is to…", options: ["Lower the GPA", "Target a module and a checkpoint", "Report it to employers"], answer: 1 }],
  },
];

// ---- Reusable quiz (reports when all answers are correct) -----------------
function QuizBlock({ quiz, testId, onPassed }) {
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState(false);
  const all = quiz.every((_, i) => answers[i] != null);
  const correct = quiz.filter((q, i) => answers[i] === q.answer).length;
  const passed = checked && correct === quiz.length;
  useEffect(() => { if (passed) onPassed?.(); }, [passed]); // eslint-disable-line

  return (
    <div className="mt-5 rounded-xl bg-slate-50 p-4" data-testid={testId}>
      <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Module quiz</p>
      <div className="mt-3 space-y-4">
        {quiz.map((q, i) => (
          <div key={i}>
            <p className="text-sm font-semibold text-[#003057]">{i + 1}. {q.q}</p>
            <div className="mt-2 flex flex-col gap-1.5">
              {q.options.map((opt, oi) => {
                const sel = answers[i] === oi;
                const showCorrect = checked && oi === q.answer;
                const showWrong = checked && sel && oi !== q.answer;
                return (
                  <button key={oi} type="button" onClick={() => !passed && setAnswers((a) => ({ ...a, [i]: oi }))}
                    data-testid={`${testId}-q${i}-opt${oi}`}
                    className={`flex items-center justify-between rounded-lg border px-3 py-1.5 text-left text-sm transition-colors ${
                      showCorrect ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                        : showWrong ? "border-red-300 bg-red-50 text-red-700"
                        : sel ? "border-[#005291] bg-[#005291]/5 text-[#003057]"
                        : "border-slate-200 bg-white text-slate-600 hover:border-[#005291]/40"}`}>
                    {opt}
                    {showCorrect && <CheckCircle2 className="h-4 w-4" />}
                    {showWrong && <XCircle className="h-4 w-4" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {!checked ? (
        <Button size="sm" disabled={!all} onClick={() => setChecked(true)} className="mt-3 rounded-full bg-[#78be21] font-semibold text-[#00243f] hover:bg-[#6aac1c]" data-testid={`${testId}-check`}>Check answers</Button>
      ) : passed ? (
        <p className="mt-3 text-sm font-semibold text-emerald-700" data-testid={`${testId}-passed`}>All correct — nicely done.</p>
      ) : (
        <p className="mt-3 text-sm font-semibold text-amber-700" data-testid={`${testId}-retry`}>You got {correct}/{quiz.length}. <button className="ml-1 text-[#005291] underline" onClick={() => { setChecked(false); setAnswers({}); }}>Try again</button></p>
      )}
    </div>
  );
}

// ---- Module section: video (no-skip) → Next → quiz → continue -------------
function ModuleSection({ m, index, onComplete }) {
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [phase, setPhase] = useState("video"); // video | quiz
  const [quizPassed, setQuizPassed] = useState(false);
  const watched = progress >= 100;

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setProgress((p) => {
      const n = Math.min(100, p + 8);
      if (n >= 100) { clearInterval(id); setPlaying(false); }
      return n;
    }), 350);
    return () => clearInterval(id);
  }, [playing]);

  return (
    <Card className="p-6 sm:p-8 card-elevate" data-testid={`module-${m.id}`}>
      <p className="text-xs font-bold uppercase tracking-widest text-[#78be21]">Module {index + 1} of {MODULES.length}</p>
      <h2 className="mt-1 font-heading text-xl font-extrabold text-[#003057]">{m.title}</h2>
      <p className="text-xs text-slate-400">~{m.minutes} min · video + quiz</p>

      <div className="relative mt-5 flex aspect-video w-full flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-slate-300 bg-slate-50" data-testid={`video-${m.id}`}>
        <PlayCircle className="h-10 w-10 text-slate-400" />
        <p className="mt-2 text-sm font-semibold text-slate-400">Training video coming soon</p>
        {!watched && (
          <Button size="sm" disabled={playing} onClick={() => setPlaying(true)} className="mt-3 rounded-full bg-[#003057] font-semibold text-white hover:bg-[#00243f]" data-testid={`play-${m.id}`}>
            {playing ? "Playing…" : progress > 0 ? "Resume" : "Play video"}
          </Button>
        )}
        {watched && <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-800" data-testid={`watched-${m.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Watched in full</span>}
        <div className="absolute bottom-0 left-0 h-1.5 w-full bg-slate-200"><div className="h-full bg-[#78be21] transition-all" style={{ width: `${progress}%` }} /></div>
        <p className="absolute right-2 top-2 rounded bg-white/80 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">No fast-forward · {Math.round(progress)}%</p>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-slate-600">{m.summary}</p>
      <ul className="mt-3 space-y-1.5">
        {m.points.map((p, i) => (<li key={i} className="flex gap-2 text-sm text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#78be21]" /> {p}</li>))}
      </ul>

      {phase === "video" && (
        watched ? (
          <Button onClick={() => setPhase("quiz")} className="mt-5 rounded-full bg-[#005291] font-semibold text-white hover:bg-[#003f70]" data-testid={`next-quiz-${m.id}`}>
            Next: Module quiz <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <p className="mt-5 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-400" data-testid={`quiz-locked-${m.id}`}><Lock className="h-4 w-4" /> Watch the full video to continue to the quiz.</p>
        )
      )}

      {phase === "quiz" && (
        <>
          <QuizBlock quiz={m.quiz} testId={`quiz-${m.id}`} onPassed={() => setQuizPassed(true)} />
          <Button disabled={!quizPassed} onClick={onComplete} className="mt-4 rounded-full bg-[#78be21] font-semibold text-[#00243f] hover:bg-[#6aac1c]" data-testid={`complete-${m.id}`}>
            Mark complete & continue <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </>
      )}
    </Card>
  );
}

// ---- Syllabus SLO mapping (required) --------------------------------------
function SyllabusSection({ mappings, setMappings, onComplete }) {
  const [syText, setSyText] = useState("");
  const [mapBusy, setMapBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const runMap = async (text) => {
    setMapBusy(true);
    try {
      const { data } = await api.post("/certification/syllabus-map", { text });
      setMappings(data.mappings); setConfirmed(false);
      toast.success(data.ai_available ? "AI suggestions ready — review & override." : "AI unavailable — set mappings manually.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setMapBusy(false); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setMapBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/certification/syllabus-extract", fd);
      setSyText(data.text); await runMap(data.text);
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); setMapBusy(false); }
  };
  const toggle = (i) => { setMappings((ms) => ms.map((m, idx) => idx === i ? { ...m, addressed: !m.addressed } : m)); setConfirmed(false); };

  return (
    <Card className="p-6 sm:p-8 card-elevate" data-testid="syllabus-mapping">
      <p className="text-xs font-bold uppercase tracking-widest text-[#78be21]">Required exercise</p>
      <h2 className="mt-1 flex items-center gap-2 font-heading text-xl font-extrabold text-[#003057]"><FileText className="h-5 w-5 text-[#005291]" /> Map your syllabus SLOs to the 10 skills</h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">Upload or paste your course syllabus. AI suggests which transferable skills your Student Learning Outcomes address — override every one, then confirm to unlock the exam.</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-[#003057] hover:bg-slate-50" data-testid="syllabus-upload">
          <Upload className="h-3.5 w-3.5" /> Upload PDF/DOCX
          <input type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={onFile} data-testid="syllabus-file" />
        </label>
        <span className="text-xs text-slate-400">or paste below</span>
      </div>
      <textarea value={syText} onChange={(e) => setSyText(e.target.value)} rows={4} placeholder="Paste syllabus / SLO text…"
        className="mt-2 w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-700 focus:border-[#005291] focus:outline-none" data-testid="syllabus-text" />
      <Button size="sm" disabled={mapBusy || syText.trim().length < 40} onClick={() => runMap(syText)} className="mt-2 rounded-full bg-[#005291] font-semibold text-white hover:bg-[#003f70]" data-testid="syllabus-map-btn">
        {mapBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Get AI recommendations
      </Button>

      {mappings && (
        <div className="mt-4 space-y-1.5" data-testid="syllabus-results">
          {mappings.map((m, i) => (
            <div key={m.skill_id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#003057]">{m.skill_name}</p>
                {m.evidence && <p className="truncate text-[11px] text-slate-400">“{m.evidence}”</p>}
              </div>
              <button type="button" onClick={() => toggle(i)} data-testid={`map-toggle-${m.skill_id}`}
                className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold ${m.addressed ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                {m.addressed ? "Addressed" : "Not addressed"}
              </button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button size="sm" onClick={() => { setConfirmed(true); toast.success("Syllabus mapping confirmed."); }} className="rounded-full bg-[#78be21] font-semibold text-[#00243f] hover:bg-[#6aac1c]" data-testid="syllabus-confirm">
              {confirmed ? <><CheckCircle2 className="mr-1.5 h-4 w-4" /> Confirmed</> : "Confirm mapping"}
            </Button>
            <Button size="sm" disabled={!confirmed} onClick={onComplete} className="rounded-full bg-[#003057] font-semibold text-white hover:bg-[#00243f]" data-testid="syllabus-continue">
              Continue to exam <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---- Certification exam (hidden until it is the active step) ---------------
function ExamSection({ exam, mappings, onPassed }) {
  const [responses, setResponses] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const allAnswered = exam.questions.every((q) => responses[q.id] != null);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/certification/submit", { responses, syllabus_mappings: mappings || [] });
      setResult(data);
      if (data.passed) { toast.success(`Passed with ${data.score}%! Certificate issued.`); onPassed(); }
      else toast.error(`Scored ${data.score}%. ${exam.pass_pct}% needed — review the modules and retry.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 sm:p-8 card-elevate" data-testid="cert-exam">
      <p className="text-xs font-bold uppercase tracking-widest text-[#78be21]">Final step</p>
      <h2 className="mt-1 flex items-center gap-2 font-heading text-xl font-extrabold text-[#003057]"><GraduationCap className="h-5 w-5 text-[#78be21]" /> Certification Exam</h2>
      <p className="mt-1 text-sm text-slate-500">{exam.total} questions · {exam.pass_pct}% to pass. You can retake it anytime.</p>

      <div className="mt-5 space-y-5">
        {exam.questions.map((q, i) => (
          <div key={q.id} data-testid={`exam-${q.id}`}>
            <p className="text-sm font-semibold text-[#003057]">{i + 1}. {q.question}</p>
            <div className="mt-2 grid grid-cols-1 gap-1.5">
              {q.options.map((opt, oi) => {
                const sel = responses[q.id] === oi;
                return (
                  <button key={oi} type="button" onClick={() => setResponses((r) => ({ ...r, [q.id]: oi }))}
                    data-testid={`exam-${q.id}-opt${oi}`}
                    className={`rounded-lg border px-3 py-1.5 text-left text-sm transition-colors ${sel ? "border-[#005291] bg-[#005291] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#005291]/40"}`}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {result && (
        <div className={`mt-5 rounded-xl p-4 text-sm font-semibold ${result.passed ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="exam-result">
          You scored {result.score}% ({result.correct}/{result.total}). {result.passed ? "Congratulations — you're certified!" : `You need ${result.pass_pct}% to pass.`}
        </div>
      )}

      <Button disabled={!allAnswered || busy} onClick={submit} className="mt-5 rounded-full bg-[#003057] font-semibold text-white hover:bg-[#00243f]" data-testid="exam-submit">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Submit exam
      </Button>
    </Card>
  );
}

// ---- Course outline (right rail) ------------------------------------------
function Outline({ sections, step, done, unlocked, onGoto }) {
  return (
    <Card className="p-4 lg:sticky lg:top-6" data-testid="course-outline">
      <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Course outline</p>
      <ol className="space-y-1">
        {sections.map((s, i) => {
          const isDone = done.has(i);
          const isCurrent = i === step;
          const open = unlocked(i);
          const Icon = isDone ? CheckCircle2 : isCurrent ? PlayCircle : open ? Circle : Lock;
          return (
            <li key={s.id}>
              <button
                disabled={!open}
                onClick={() => open && onGoto(i)}
                data-testid={`outline-${s.id}`}
                className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                  isCurrent ? "bg-[#003057]/5 font-bold text-[#003057]"
                    : isDone ? "font-medium text-[#3d6b0e] hover:bg-slate-50"
                    : open ? "text-slate-500 hover:bg-slate-50"
                    : "cursor-not-allowed text-slate-300"}`}>
                <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${isDone ? "text-[#78be21]" : isCurrent ? "text-[#003057]" : open ? "text-slate-400" : "text-slate-300"}`} />
                <span>{s.title}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

function CertificateCard({ cert }) {
  const ref = useRef(null);
  const download = async () => {
    try { await downloadElementPdf(ref.current, `TSB Certificate - ${cert.name || "instructor"}.pdf`); toast.success("Certificate downloaded."); }
    catch { toast.error("Couldn't generate the PDF."); }
  };
  return (
    <Card className="overflow-hidden card-elevate" data-testid="certificate-card">
      <div ref={ref} className="relative border-8 border-double border-[#003057]/20 bg-white p-8 text-center">
        <img src={GPA_LOGO} alt="GPA+" className="mx-auto h-10 w-auto" />
        <p className="mt-4 text-xs font-bold uppercase tracking-[0.3em] text-[#78be21]">Certificate of Completion</p>
        <h2 className="mt-2 font-heading text-2xl font-extrabold text-[#003057]">TSB Benchmark Certification</h2>
        <p className="mt-4 text-sm text-slate-500">This certifies that</p>
        <p className="mt-1 font-heading text-xl font-bold text-[#003057]">{cert.name}</p>
        <p className="mt-1 text-sm text-slate-500">has completed the Transferable Skills Benchmark instructor certification with a score of <span className="font-bold text-[#003057]">{cert.score}%</span>.</p>
        <div className="mt-5 flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs text-slate-500">
          <span>Certificate ID: <span className="font-mono-num font-bold text-[#003057]">{cert.cert_id}</span></span>
          <span>Issued: <span className="font-semibold text-[#003057]">{cert.issued_at?.slice(0, 10)}</span></span>
          <span>Valid through: <span className="font-semibold text-[#003057]">{cert.expires_at?.slice(0, 10)}</span></span>
        </div>
        <p className="mt-4 text-[11px] text-slate-400">Valid for 3 years. Renew with the updated course to maintain certification.</p>
      </div>
      <div className="flex justify-end border-t border-slate-100 p-3" data-pdf-hide>
        <Button variant="outline" onClick={download} className="rounded-full font-semibold" data-testid="download-cert-btn"><FileDown className="mr-2 h-4 w-4" /> Download certificate</Button>
      </div>
    </Card>
  );
}

// ---- Page -----------------------------------------------------------------
export default function AdvisorCertification() {
  const [status, setStatus] = useState(null);
  const [exam, setExam] = useState(null);
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(new Set());
  const [mappings, setMappings] = useState(null);

  const loadStatus = () => api.get("/certification/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ certified: false, valid: false }));
  useEffect(() => {
    loadStatus();
    api.get("/certification/exam").then(({ data }) => setExam(data)).catch(() => setExam(null));
    api.get("/certification/progress").then(({ data }) => {
      if (Array.isArray(data.done)) setDone(new Set(data.done));
      if (typeof data.step === "number") setStep(data.step);
    }).catch(() => {});
  }, []);

  const sections = [
    ...MODULES.map((m) => ({ id: m.id, title: `Module ${MODULES.indexOf(m) + 1} · ${m.title}`, type: "module", m })),
    { id: "syllabus", title: "Syllabus SLO Mapping", type: "syllabus" },
    { id: "exam", title: "Certification Exam", type: "exam" },
  ];
  const unlocked = (i) => i === 0 || done.has(i - 1);
  const saveProgress = (doneSet, s) => api.post("/certification/progress", { done: [...doneSet], step: s }).catch(() => {});
  const complete = (i) => {
    const nd = new Set(done); nd.add(i);
    const ns = Math.min(i + 1, sections.length - 1);
    setDone(nd); setStep(ns); saveProgress(nd, ns);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goto = (i) => { if (unlocked(i)) { setStep(i); saveProgress(done, i); window.scrollTo({ top: 0, behavior: "smooth" }); } };

  const valid = status?.valid;
  const active = sections[step];

  return (
    <DashboardShell title="TSB Benchmark Certification" subtitle="Instructor certification · complete each section in order to unlock the exam" nav={ADVISOR_NAV}>
      {!status && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {status && (
        <div className="space-y-6">
          {/* Overview + compensation note */}
          <Card className="p-6 card-elevate">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#003057] text-white"><Award className="h-6 w-6" /></div>
              <div>
                <h2 className="font-heading text-xl font-extrabold text-[#003057]">Become a certified TSB instructor</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">Work through five modules (watch each video in full, then pass its quiz), complete the syllabus SLO mapping, and pass the exam ({exam?.pass_pct || 70}% required). Certification is valid for <span className="font-semibold">3 years</span>.</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex items-start gap-2 rounded-xl border border-[#78be21]/30 bg-[#78be21]/5 p-4">
                <DollarSign className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#5fa114]" />
                <p className="text-sm text-slate-700"><span className="font-bold">Potential additional compensation.*</span> Certification may qualify instructors for additional compensation or stipends.<span className="mt-1 block text-xs text-slate-400">*Compensation is proposed and <span className="font-semibold">not yet approved</span>; subject to institutional review.</span></p>
              </div>
              <div className="flex items-start gap-2 rounded-xl border border-slate-200 p-4">
                <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#005291]" />
                <p className="text-sm text-slate-700"><span className="font-bold">3-year validity.</span> Your certificate shows an issue and expiry date. We'll prompt renewal with the latest course before it lapses.</p>
              </div>
            </div>
          </Card>

          {valid && status.certificate && (
            <>
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800" data-testid="certified-banner">
                <CheckCircle2 className="h-5 w-5" /> You're certified through {status.certificate.expires_at?.slice(0, 10)}.
                <span className="ml-1 inline-flex items-center gap-1 text-xs font-normal text-emerald-700"><RotateCcw className="h-3.5 w-3.5" /> Retaking re-issues a fresh 3-year certificate.</span>
              </div>
              <CertificateCard cert={status.certificate} />
            </>
          )}

          {/* Guided layout: content left, outline right */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
            <div className="order-2 lg:order-1" data-testid="course-content">
              {active.type === "module" && <ModuleSection m={active.m} index={MODULES.findIndex((x) => x.id === active.id)} onComplete={() => complete(step)} />}
              {active.type === "syllabus" && <SyllabusSection mappings={mappings} setMappings={setMappings} onComplete={() => complete(step)} />}
              {active.type === "exam" && exam && <ExamSection exam={exam} mappings={mappings} onPassed={loadStatus} />}
            </div>
            <div className="order-1 lg:order-2">
              <Outline sections={sections} step={step} done={done} unlocked={unlocked} onGoto={goto} />
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
